import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  CheckCircle2,
  Briefcase,
  Target,
  BadgeCheck,
  Cpu,
  Layers,
  ShieldCheck,
} from "lucide-react";

import Seo from "../../Components/SEO/Seo";
import Container from "../../Components/Container/Container";
import Button from "../../Components/Button/Button";
import {
  RECRUITMENT_TAGS,
  CORE_OFFERINGS,
  METRIC_HIGHLIGHTS,
  STRATEGIC_PILLARS,
} from "../../Data/recruitmentData";

import "../../Style/services/data-validation.css";
import "../../Style/services/b2b-appointment.css";
import "../../Style/services/recruitment.css";

const cardHover = {
  initial: { y: 0, opacity: 0.95 },
  hover: { y: -5, opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
};

export default function Recruitment() {
  const handleConsultation = () => {
    window.history.pushState({}, "", "/contact");
    window.dispatchEvent(new Event("popstate"));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="validation-page recruit-page">
      <Seo
        title="Recruitment & Staffing Consulting | Conectify Tech"
        description="Your trusted partner in recruitment and staffing consulting. Faster hiring, lower turnaround time, and high-quality workforce placement across the USA."
      />

      {/* 1. Hero Section */}
      <section className="val-hero recruit-hero">
        <Container className="val-hero__container">
          <motion.div
            className="val-hero__content"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <div className="section-eyebrow">
              <Sparkles size={14} />
              <span>Human Capital & Talent Advisory</span>
            </div>

            <h1 className="val-hero__title">
              Your Trusted Partner in{" "}
              <span className="text-gradient">Recruitment & Staffing Consulting</span>
            </h1>

            <p className="val-hero__lead">
              Finding the right talent for your organization is crucial for success, and at
              Conectify Tech, we understand the challenges of recruitment in today’s competitive job
              market. As a leading Recruitment Consultant in the United States, we are dedicated to
              connecting businesses with the best candidates to meet their unique needs.
            </p>

            <div className="val-hero__tags">
              {RECRUITMENT_TAGS.map((tag) => {
                const Icon = tag.icon;
                return (
                  <span key={tag.label} className="val-tag">
                    <Icon size={13} className="text-primary" />
                    <span>{tag.label}</span>
                  </span>
                );
              })}
            </div>

            <div className="val-hero__actions">
              <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
                Hire Qualified Talent
              </Button>
              <Button variant="secondary" onClick={handleConsultation}>
                Explore Workforce Models
              </Button>
            </div>
          </motion.div>

          {/* Interactive Talent Acquisition Engine HUD */}
          <motion.div
            className="val-hero__card-wrap"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            <div className="recruit-engine-hud">
              {/* Top Engine Header */}
              <div className="recruit-engine-hud__top">
                <div className="flex items-center gap-2">
                  <Cpu size={18} className="text-primary" />
                  <span className="font-bold text-sm">Talent Acquisition Infrastructure</span>
                </div>
                <span className="hud-live-tag">
                  <span className="live-dot" /> SLA Active
                </span>
              </div>

              {/* 4-Stage Candidate Precision Lifecycle */}
              <div className="recruit-lifecycle">
                <span className="recruit-lifecycle__label">Candidate Precision Lifecycle</span>
                <div className="recruit-lifecycle__steps">
                  <div className="lifecycle-step is-complete">
                    <span className="step-num">01</span>
                    <span className="step-txt">Criteria & Role Calibration</span>
                  </div>
                  <div className="lifecycle-step is-complete">
                    <span className="step-num">02</span>
                    <span className="step-txt">Multi-Layer Domain Screening</span>
                  </div>
                  <div className="lifecycle-step is-active">
                    <span className="step-num">03</span>
                    <span className="step-txt">Pre-Vetted Shortlist (&lt; 72h)</span>
                  </div>
                  <div className="lifecycle-step">
                    <span className="step-num">04</span>
                    <span className="step-txt">Seamless Onboarding & Retention</span>
                  </div>
                </div>
              </div>

              {/* Live Operational Metrics Matrix */}
              <div className="recruit-engine-stats">
                <div className="engine-stat-cell">
                  <span className="stat-title">Shortlist Window</span>
                  <strong className="stat-value text-primary">&lt; 72 Hours</strong>
                  <span className="stat-sub">From intake to review</span>
                </div>
                <div className="engine-stat-cell">
                  <span className="stat-title">Interview to Offer</span>
                  <strong className="stat-value text-success">4 : 1 Ratio</strong>
                  <span className="stat-sub">High-precision matches</span>
                </div>
                <div className="engine-stat-cell">
                  <span className="stat-title">First-Year Retention</span>
                  <strong className="stat-value text-primary">98.4%</strong>
                  <span className="stat-sub">Placement stability</span>
                </div>
              </div>

              {/* Verified Placement Specializations */}
              <div className="recruit-domains">
                <span className="domains-label">Dedicated Hiring Practice Groups:</span>
                <div className="domains-chips">
                  <span className="domain-chip">
                    <Briefcase size={12} /> Executive Leadership
                  </span>
                  <span className="domain-chip">
                    <Layers size={12} /> Software & Cloud Arch
                  </span>
                  <span className="domain-chip">
                    <Target size={12} /> B2B Go-To-Market
                  </span>
                  <span className="domain-chip">
                    <ShieldCheck size={12} /> Compliance & Data
                  </span>
                </div>
              </div>

              {/* Bottom Guarantee Banner */}
              <div className="recruit-engine-hud__footer">
                <BadgeCheck size={16} className="text-success" />
                <span>Comprehensive Background Audits & Skill Validation Guaranteed</span>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* 2. Core Offerings: Recruitment Services & Staffing Solutions */}
      <section className="recruit-offerings-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">End-to-End Capabilities</span>
            <h2 className="section-title">Specialized Workforce Architectures</h2>
            <p className="section-desc max-w-2xl mx-auto">
              Whether you need critical executive leaders or on-demand contingent teams, we adapt to
              your operational constraints.
            </p>
          </div>

          <div className="recruit-offerings-grid">
            {CORE_OFFERINGS.map((offer) => {
              const Icon = offer.icon;
              return (
                <div key={offer.title} className="recruit-offering-card">
                  <div className="recruit-offering-card__header">
                    <div className="offering-icon">
                      <Icon size={24} />
                    </div>
                    <span className="offering-badge">{offer.badge}</span>
                  </div>

                  <h3 className="offering-title">{offer.title}</h3>
                  <p className="offering-desc">{offer.desc}</p>

                  <div className="offering-deliverables">
                    {offer.deliverables.map((item) => (
                      <div key={item} className="deliverable-item">
                        <CheckCircle2 size={15} className="text-primary flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 3. 99% Satisfaction & Performance Benchmark Strip */}
      <section className="recruit-benchmark-section">
        <Container>
          <div className="recruit-benchmark-card">
            {/* Top Anchor: Balanced 2-Column Hero Header */}
            <div className="recruit-benchmark-hero">
              <div className="benchmark-ring-badge">
                <div className="benchmark-ring-inner">
                  <span className="benchmark-ring-val text-gradient">99%</span>
                  <span className="benchmark-ring-lbl">Satisfaction</span>
                </div>
                <div className="benchmark-ring-glow" aria-hidden="true" />
              </div>

              <div className="benchmark-hero-copy">
                <span className="section-eyebrow">
                  <BadgeCheck size={14} className="text-primary" />
                  <span>Client Satisfaction Rate</span>
                </span>
                <h3 className="benchmark-title">
                  A Testament To The <span className="text-gradient">Value We Provide</span>
                </h3>
                <p className="benchmark-desc">
                  Experience the best in recruitment and staffing services. Partner with Conectify
                  Tech for better talent acquisition, rigorous candidate pre-screening, and improved hiring outcomes.
                </p>
              </div>
            </div>

            {/* Bottom 4 Key Metric Cards */}
            <div className="recruit-metrics-grid">
              {METRIC_HIGHLIGHTS.map((m) => (
                <div key={m.label} className="recruit-metric-box">
                  <div className="recruit-metric-box__top">
                    <span className="recruit-metric-box__val text-gradient">{m.value}</span>
                  </div>
                  <strong className="recruit-metric-box__lbl">{m.label}</strong>
                  <span className="recruit-metric-box__sub">{m.sub}</span>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* 4. Strategic Value Pillars */}
      <section className="recruit-pillars-section">
        <Container>
          <div className="section-head text-center">
            <span className="section-eyebrow">Strategic Advantage</span>
            <h2 className="section-title">Why Leading Enterprises Choose Conectify Tech</h2>
            <p className="section-desc max-w-2xl mx-auto">
              We blend market analytics, comprehensive background assessments, and personalized
              advisory to deliver unmatched talent fits.
            </p>
          </div>

          <div className="recruit-pillars-grid">
            {STRATEGIC_PILLARS.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={pillar.title}
                  className="val-why-card recruit-pillar-card"
                  variants={cardHover}
                  initial="initial"
                  whileHover="hover"
                >
                  <div className="val-why-card__icon">
                    <Icon size={22} />
                  </div>
                  <h4>{pillar.title}</h4>
                  <p>{pillar.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 5. Bottom Call to Action */}
      <section className="b2b-cta">
        <Container>
          <div className="b2b-cta__card">
            <h2>Accelerate Your Hiring Pipeline with Vetted Professionals</h2>
            <p>
              Stop losing productive quarters to extended talent searches. Connect with our senior
              recruitment consultants today to secure your shortlist.
            </p>
            <Button icon={<ArrowRight size={18} />} onClick={handleConsultation}>
              Initiate Talent Requisition
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
}